import { action, observable } from 'mobx';

import { appConfig } from 'cd-eft/config/AppConfig';
import * as SETAPI from 'cd-eft/services/eftSetApi';
import * as SET from 'cd-eft/_codegen_/openapi-generator/eft-set';
import * as geometryUtils from 'cd-eft/components/Design/BreaklineTools/utils/geometryUtils';

import * as esriProjection from '@arcgis/core/geometry/projection';
import SpatialReference from '@arcgis/core/geometry/SpatialReference';
import Extent from '@arcgis/core/geometry/Extent';
import Point from '@arcgis/core/geometry/Point';

import { XyzSurvey } from './XyzSurvey';
import { GroundPoint } from './GroundPoint';
import { SurfaceBreakline } from './SurfaceBreakline';
import { SurfaceBorder } from './SurfaceBorder';
import { SyncObject } from 'cd-eft/framework/SyncObject';
import { SyncSet } from 'cd-eft/framework/SyncSet';
import { Polygon, Polyline, esriGeometryEngine } from 'cd-eft/utils/esriTypes';
import { GroundPointEditAdapter } from './GroundPointEditAdapter';
import stores from 'cd-eft/models/Stores';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import MapView from '@arcgis/core/views/MapView';
import { doesPolylineSelfIntersect } from 'cd-eft/utils/gisUtils';

const { projectsLibraryStore: libraryStore, designStore } = stores.AppStore;

export class SurfaceDef {
  readonly eft_type: string = 'SurfaceDef';
  orginalBorder: SurfaceBorder | undefined = void 0;


  @observable id: number = 0;
  @observable title: string = '';
  @observable wkid: number = 0;
  @observable surveys: XyzSurvey[] = [];
  @observable surveyEditables: XyzSurvey[] = [];

  @observable pointsModifiedByEditing: GroundPoint[] = [];
  @observable contourIntervalChanged: boolean = false;


  borderRebuild = false;


  get contourIntervalModified(): boolean {
    return this.contourIntervalChanged;
  }

  set contourIntervalModified(value: boolean) {
    this.contourIntervalChanged = value;
  }

  get countPointsModified(): number {
    return this.pointsModifiedByEditing.length;
  }
  dictGroupPoints = {};

  private _breaklinesSync: SyncSet<SurfaceBreakline> = new SyncSet();
  get breaklines(): Readonly<SurfaceBreakline[]> {
    return this._breaklinesSync.getItems();
  }
  get breaklinesSyncSet(): SyncSet<SurfaceBreakline> {
    return this._breaklinesSync;
  }

  get breaklinesPendingEditCount(): number {
    return this._breaklinesSync.getPendingEditsCount();
  }


  private _borderSync: SyncObject<SurfaceBorder> = SyncObject.constructSynchronized(new SurfaceBorder());
  get border(): SurfaceBorder {
    return this._borderSync.current!;
  }
  set border(value: SurfaceBorder) {
    this._borderSync.update(value);
  }
  get isBorderModified(): boolean {
    return this._borderSync.syncStatus !== 'synchronized';
  }

  constructor() { }

  getAllGroundPoints(): GroundPoint[] {
    const result: GroundPoint[] = [];
    for (const survey of this.surveys) {
      for (const groundPoint of survey.groundPoints) {
        result.push(groundPoint);
      }
    }
    return result;
  }

  getAllGroundPointEditables(currentSurveyId, pointDescriptionFilter): GroundPointEditAdapter[] {
    const result: GroundPointEditAdapter[] = [];
    for (const survey of this.surveyEditables) {
      for (const groundPoint of survey.groundPoints) {
        groundPoint.filtered =
          (currentSurveyId === survey.surveyId && groundPoint.description === pointDescriptionFilter) ||
            (currentSurveyId === survey.surveyId && pointDescriptionFilter === "All") ||
            (currentSurveyId === "all-surveys" && pointDescriptionFilter === "All") ||
            (currentSurveyId === "all-surveys" && groundPoint.description === pointDescriptionFilter) ||
            (currentSurveyId === "")
            ? true : false;

        result.push(groundPoint as GroundPointEditAdapter);
      }
    }
    return result;
  }

  computeExtent(spatialReference: SpatialReference): Extent {
    const groundPoints = this.getAllGroundPoints();
    const points: Point[] = [];
    for (const groundPoint of groundPoints) {
      points.push(groundPoint.generateGeometry(spatialReference));
    }
    const extent = geometryUtils.computeExtent(points, spatialReference);
    return extent;
  }

  @action
  applyBorderOnSurveyEditables(borderPolygon: Polygon, borderPolyline: Polyline) {
    for (const survey of this.surveyEditables) {
      survey.groundPoints.forEach((pnt) => {
        const pntApt = pnt as GroundPointEditAdapter;
        pntApt?.applyBorder(borderPolygon, borderPolyline);
      });
    }

    this.logPendingEdits();
  }


  @action
  logPendingEdits() {
    this.pointsModifiedByEditing.length = 0;
    for (const survey of this.surveyEditables) {
      survey.groundPoints.forEach((pnt) => {
        const pntApt = pnt as GroundPointEditAdapter;
        const entry = this.dictGroupPoints[pntApt.id.toString()];
        if (
          pntApt.pointName === entry.pointName &&
          pntApt.featureDescription === entry.featureDescription &&
          pntApt.visible === entry.visible &&
          pntApt.tinable === entry.tinable
        ) {
          // same, do nothing
        } else {
          const changedPoint = {};
          Object.assign(changedPoint, pntApt);
          this.pointsModifiedByEditing.push(changedPoint as GroundPointEditAdapter);
        }
      });
    }
  }

  clearBorderSyncStatus() {
    this._borderSync.acceptChanges();
  }

  clearBreaklinesSyncStatus() {
    this._breaklinesSync.purgeDeletes();
    this._breaklinesSync.clearSyncStatus();
  }

  clearGroundPointsSyncStatus() {
    this.pointsModifiedByEditing.length = 0;
  }

  private async generateDefaultBorder(): Promise<SurfaceBorder> {
    await esriProjection.load();

    const borderDto = (await SETAPI.SurfaceController.genarateBorderUsingGET(this.id)).data;

    const border = new SurfaceBorder();
    border.wkid = this.wkid;

    const allGroundPoints = this.getAllGroundPoints();
    for (const p of borderDto.points!) {
      let found: GroundPoint | undefined = undefined;
      for (const gp of allGroundPoints) {
        //TODO: figure out why z values of border points and ground points don't always match
        if (gp.wkid === border.wkid && gp.x === p.x && gp.y === p.y) {
          found = gp;
          break;
        }
      }
      if (found === undefined) {
        throw new Error('Unexpected result. Could not find corresponding ground point in collection.');
      }
      border.borderPoints.push(found);
    }

    return border;
  }

  // async generateDefaultBorderConvexHull(): Promise<SurfaceBorder> {
  //   // const spatialReference = new SpatialReference({ wkid: this.wkid });
  //   // const groundPoints = this.getAllGroundPoints();
  //   // const pointList: Point[] = [];
  //   // for (const groundPoint of groundPoints) {
  //   //   if (groundPoint.visible && groundPoint.tinable) {
  //   //     pointList.push(groundPoint.generateGeometry(spatialReference));
  //   //   }
  //   // }
  //   // const border = new SurfaceBorder();
  //   // border.wkid = this.wkid;
  //   // const hull = esriGeometryEngine.convexHull(pointList, true)[0] as Polygon;
  //   // for (let i = 0; i < hull.rings[0].length; i++) {
  //   //   const point = hull.getPoint(0, i);
  //   //   const borderPoint = new SurfaceBorderPoint();
  //   //   borderPoint.wkid = this.wkid;
  //   //   borderPoint.x = point.x;
  //   //   borderPoint.y = point.y;
  //   //   borderPoint.z = point.z;
  //   //   border.borderPoints.push(borderPoint);
  //   // }
  //   // return border;

  // }

  static fromDto(wkid: number, dto: any, surveyDtos?: SET.SurveyPoints[]): SurfaceDef {
    if (dto.surfaceDefClassId !== 1 && dto.surfaceDefClassId !== 2) {
      throw new Error('Invalid argument. This dto is not correct SurfaceDef class type.');
    }
    const result = new SurfaceDef();
    if (dto.surfaceDefId) result.id = dto.surfaceDefId;
    if (dto.surfaceDefTitle) result.title = dto.surfaceDefTitle;

    result.wkid = wkid;

    if (surveyDtos && surveyDtos.length > 0) {
      for (const surveyDto of surveyDtos) {
        // only convert XyzSurveys, other survey types will be ignored for now
        if (surveyDto.surveyTypeId === 1) {
          const xyzSurvey = XyzSurvey.fromDto(surveyDto);
          result.surveys.push(xyzSurvey);
        }
      }
    }
    SurfaceDef.createDictGroundPoints(result);

    return result;
  }

  static async fetchSurfaceDefWithBreaklinesAndUpdatedBorder(sessionUuid: string): Promise<SurfaceDef> {
    const response = await SETAPI.SurfaceDefController.getSurfaceDefBySessionUsingGET(sessionUuid);
    const surfaceDefId = response.data[0].surfaceDefId!;

    const surfaceDef = await this.fetchSurfaceDef(surfaceDefId);
    if (designStore.currentSurface.surfaceDefClassName !== 'dem') {
      await this.fetchBreaklines(surfaceDefId, surfaceDef);
      await this.fetchBorderPoints(sessionUuid, surfaceDef);
    }

    return surfaceDef;
  }

  static async fetchBreaklines(surfaceDefId, surfaceDef) {
    //load up saved breaklines
    const jsonResponse = await SETAPI.BreaklineController.findAllBreaklineBySurfaceDefIdUsingGET(surfaceDefId);

    //display breaklines on the surface
    if (jsonResponse.data && jsonResponse.data.breakLineDataResponse?.length) {
      for (const breakLineDataResponse of jsonResponse.data.breakLineDataResponse) {
        const breaklineObj = JSON.parse(breakLineDataResponse.json!);
        const surfaceBreakline = new SurfaceBreakline(breakLineDataResponse.breaklineId);
        const allGroundPoints = surfaceDef.getAllGroundPoints();
        for (const pointObj of breaklineObj.points) {
          const groundPoint = allGroundPoints.find((item) => {
            return (
              item.name === pointObj.name &&
              item.description === pointObj.description &&
              item.x === pointObj.x &&
              item.y === pointObj.y &&
              item.z === pointObj.z
            );
          });
          if (!groundPoint) {
            throw new Error('Unexpected result.');
          }
          surfaceBreakline.groundPoints.push(groundPoint);
        }
        surfaceDef._breaklinesSync.loadItem(surfaceBreakline);
      }
    }
  }

  static async fetchBorderPoints(sessionUuid: string, surfaceDef) {
    //load up saved border points
    const jsonResponse = await SETAPI.DesignElementController.getAllDesignElementsBySessionAndTypeUsingGET(
      sessionUuid,
      'Surface',
      true
    );

    if (jsonResponse.data && jsonResponse.data.length) {
      const data = jsonResponse.data[0];
      const surfaceJson = data.json!;
      const surfaceObject = JSON.parse(surfaceJson);

      //display updated border on the surface
      if (surfaceObject.borderVector && surfaceObject.borderVector.length) {
        const borderPointsArray = surfaceObject.borderVector as any[];
        const surfaceBorder = new SurfaceBorder();
        surfaceBorder.wkid = appConfig.tempHardCodes.wkid;

        const allGroundPoints = surfaceDef.getAllGroundPoints();
        for (const borderPoint of borderPointsArray) {
          const groundPoint = allGroundPoints.find((item) => {
            return item.x === borderPoint.x && item.y === borderPoint.y && item.z === borderPoint.z;
          });
          if (!groundPoint) {
            throw new Error(
              `BorderPoint: {x: ${borderPoint.x}, y: ${borderPoint.y}, z: ${borderPoint.z} } does not exist in ground points`
            );
          }
          surfaceBorder.borderPoints.push(groundPoint);
          surfaceDef._borderSync = SyncObject.constructSynchronized(surfaceBorder);
        }

        // save the original border for comparisons later
        const newBorder = new SurfaceBorder();
        newBorder.wkid = surfaceDef.border.wkid;
        newBorder.borderPoints.push(...surfaceDef.border.borderPoints);
        surfaceDef.orginalBorder = newBorder;
      }
    }

    return surfaceDef;
  }

  static async fetchSurfaceDef(surfaceDefId: number): Promise<SurfaceDef> {
    await esriProjection.load();

    const surfaceDefDto = (await SETAPI.SurfaceDefController.getSurfaceDefUsingGET(surfaceDefId)).data;

    const surfaceDefSurveyMetadataDto: any = (
      await SETAPI.SurfaceDefController.getSurfaceDefSurveyMetadataUsingGET(surfaceDefId)
    ).data;
    // console.log(surfaceDefSurveyMetadataDto);

    const surveyDtos: SET.SurveyPoints[] = [];
    if (surfaceDefSurveyMetadataDto.surveyMetadata) {
      surfaceDefSurveyMetadataDto.surveyMetadata.sort(SurfaceDef.sortBySurveyName);
      for (const surveyMetadataDto of surfaceDefSurveyMetadataDto.surveyMetadata) {
        const surveyId: string = surveyMetadataDto.surveyId;
        const surveyDto = (await SETAPI.SetSurveyController.getSurveyUsingGET(surveyId)).data;
        // console.log('surveyDto', surveyDto);
        surveyDtos.push(surveyDto);
      }
    }

    const wkid = appConfig.tempHardCodes.wkid;
    const surfaceDef = SurfaceDef.fromDto(wkid, surfaceDefDto, surveyDtos);

    // if the border is missing, generate a default border
    if (surfaceDef.border.borderPoints.length === 0) {
      // try {
      // surfaceDef.border = await surfaceDef.generateDefaultBorder();
      const border = await surfaceDef.generateDefaultBorder();
      surfaceDef._borderSync = SyncObject.constructSynchronized(border);

      // } catch (error) {
      //   console.error('Unable to generateDefaultBorder');
      //   console.error(error);
      // }
    }


    // create ground points with edits adapter
    SurfaceDef.createSurveyEditables(surfaceDef);

    return surfaceDef;
  }

  static createSurveyEditables(surfaceDef: SurfaceDef) {
    surfaceDef.surveyEditables.length = 0;
    surfaceDef.surveys.forEach((s) => {
      const surveyEditable: XyzSurvey = { ...s };
      surveyEditable.groundPoints = s.groundPoints.map((p) => new GroundPointEditAdapter(p));
      surfaceDef.surveyEditables.push(surveyEditable);
    });
  }

  static createDictGroundPoints(surfaceDef: SurfaceDef) {
    surfaceDef.dictGroupPoints = {};
    for (const survey of surfaceDef.surveys) {
      survey.groundPoints.forEach((pnt) => {
        if (pnt.id)
          surfaceDef.dictGroupPoints[pnt.id.toString()] = {
            pointName: pnt.name,
            featureDescription: pnt.description,
            visible: pnt.visible,
            tinable: pnt.tinable,
          };
      });
    }
  }

  private static sortBySurveyName(a: any, b: any): number {
    if (a.surveyName > b.surveyName) return 1;
    if (a.surveyName < b.surveyName) return -1;
    return 0;
  }

  public generatePointGeometryList(spatialReference?: SpatialReference): Point[] {
    if (!spatialReference) {
      spatialReference = libraryStore.projectSpatialReference!;
    }

    const pointList: Point[] = [];
    for (const groundPoint of this.getAllGroundPoints()) {
      const point = groundPoint.generateGeometry(spatialReference);
      pointList.push(point);
    }

    return pointList;
  }

  // following for auto border re-genearation
  private isOutsider(pointId: number) {
    let truthy = false;
    let found: GroundPoint | undefined;
    for (const survey of this.surveyEditables) {
      for (const groundPoint of survey.groundPoints) {
        if (groundPoint.id === pointId) {
          found = groundPoint;
          break;
        }
      }
      if (found) {
        break;
      }
    }
    if (found) {
      const borderPolygon = this.border.generatePolygonGeometry();
      const borderPolyline = this.border.generatePolylineGeometry();
      const pnt = found?.generateGeometry(borderPolygon.spatialReference);
      truthy = !esriGeometryEngine.contains(borderPolygon, pnt) && !esriGeometryEngine.intersects(borderPolyline, pnt);

      truthy ? console.log(`${found.name} is out.`) : console.log(`${found.name} is in.`);
    }
    return { truthy, found };
  }

  private testSelfCrossing = (newBorder: SurfaceBorder, startIndex: number) => {
    const spatialReference = libraryStore.projectSpatialReference!;

    const firstSegment = new Polyline({ paths: [[[newBorder.borderPoints[startIndex].x, newBorder.borderPoints[startIndex].y], [newBorder.borderPoints[startIndex + 1].x, newBorder.borderPoints[startIndex + 1].y]]], spatialReference });
    const secondSegment = new Polyline({ paths: [[[newBorder.borderPoints[startIndex + 1].x, newBorder.borderPoints[startIndex + 1].y], [newBorder.borderPoints[startIndex + 2].x, newBorder.borderPoints[startIndex + 2].y]]], spatialReference });

    let isSelfCrossed = false;
    for (const currentSegment of [firstSegment, secondSegment]) {
      for (let index = 0; index < newBorder.borderPoints.length - 2; index++) {
        if (index !== startIndex && index !== startIndex + 1) {
          const pntArr1 = [newBorder.borderPoints[index].x, newBorder.borderPoints[index].y];
          const pntArr2 = [newBorder.borderPoints[index + 1].x, newBorder.borderPoints[index + 1].y];
          const targetSegment = new Polyline({ paths: [[pntArr1, pntArr2]], spatialReference });
          if (esriGeometryEngine.crosses(targetSegment, currentSegment)) {
            isSelfCrossed = true;
            break;
          }
        }
      }
      if (isSelfCrossed) {
        break;
      }
    }
    return isSelfCrossed;
  }

  private findNearestBorderPoint = (inputPnt: GroundPoint) => {
    const inputPoint = inputPnt.generateGeometry(libraryStore.projectSpatialReference!);
    const resultes = esriGeometryEngine.nearestVertices(this.border.generatePolylineGeometry(), inputPoint, designStore.maxAddBorderPointDistance, 21);
    const nearestBorderPoints: { index: number, groundPoint: GroundPoint, distance: number }[] = [];

    for (const r of resultes) {
      const pnt = this.border.borderPoints[r.vertexIndex]
      nearestBorderPoints.push({ index: r.vertexIndex, groundPoint: pnt, distance: r.distance });
    }

    return nearestBorderPoints;

  }

  public async autoRegenerateBorder(pointId: number, changingTo: boolean) {
    let pointProcessingType: "regular" | "border and modify" | "border but passthrough" = "regular";
    const borderPointIndex = this.border.borderPoints.findIndex(p => p.id === pointId);

    // delete a border point
    if (borderPointIndex >= 0 && changingTo === false) {
      pointProcessingType = "border but passthrough";

      let found: GroundPoint | undefined;
      for (const survey of this.surveyEditables) {
        for (const groundPoint of survey.groundPoints) {
          if (groundPoint.id === pointId) {
            found = groundPoint;
            break;
          }
        }
        if (found) {
          break;
        }
      }

      if (this.border.borderPoints.length <= 3) {
        MessageCenter.addWarning("A border must have at least 3 points.")
        if (found) {
          found.tinable = 1;
          found.visible = 1;
        }

      } else {
        let behind: GroundPoint | undefined;
        let ahead: GroundPoint | undefined;
        if (borderPointIndex === 0) {
          behind = this.border.borderPoints[this.border.borderPoints.length - 1];
          ahead = this.border.borderPoints[1];

        } else {
          behind = this.border.borderPoints[borderPointIndex - 1];
          if (this.border.borderPoints.length === borderPointIndex + 1) {
            ahead = this.border.borderPoints[0];

          } else {
            ahead = this.border.borderPoints[borderPointIndex + 1];

          }
        }

        /** 
        const pnt = this.border.borderPoints[borderPointIndex];
        if (behind) {
          console.log(`${pnt.name} has a behind point: ${behind.name}. `);
        } else {
          console.log(`${pnt.name} doesn't have a behind point. border points total ${this.border.borderPoints.length} `);
        }

        if (ahead) {
          console.log(`${pnt.name} has an ahead point: ${ahead.name}. `);
        } else {
          console.log(`${pnt.name} doesn't have an ahead point. border points total ${this.border.borderPoints.length} `);
        }
        //*/


        const distanceToBehind = esriGeometryEngine.distance(found!.generateGeometry(libraryStore.projectSpatialReference!), behind.generateGeometry(libraryStore.projectSpatialReference!))
        let distanceToAhead = 0;

        if (distanceToBehind > designStore.maxDeleteBorderPointDistance) {
          // MessageCenter.addWarning(`The point is too far away from the behind border point, ${warningAdvice}.`)
          await MessageCenter.addWarningFromCode("Survey_25");
          this.borderRebuild = true;
          pointProcessingType = "border and modify";
          // if (found) {
          //   found.tinable = 1;
          //   found.visible = 1;
          // }

        } else {
          distanceToAhead = esriGeometryEngine.distance(found!.generateGeometry(libraryStore.projectSpatialReference!), ahead.generateGeometry(libraryStore.projectSpatialReference!))
          if (distanceToAhead > designStore.maxDeleteBorderPointDistance) {
            // MessageCenter.addWarning(`The point is too far away from the ahead border point, ${warningAdvice}.`)
            await MessageCenter.addWarningFromCode("Survey_25");
            this.borderRebuild = true;
            pointProcessingType = "border and modify";
            // if (found) {
            //   found.tinable = 1;
            //   found.visible = 1;
            // }

          } else {
            const newBorder = new SurfaceBorder();
            newBorder.wkid = this.border.wkid;
            newBorder.borderPoints.push(...this.border.borderPoints);
            newBorder.borderPoints.splice(borderPointIndex, 1);
            this.border = newBorder;
            if (this.isCurrentIdentiticalToOriginalBorder()) {
              this.clearBorderSyncStatus();
            }

            pointProcessingType = "border and modify";
            this.applyBorderOnSurveyEditables(this.border.generatePolygonGeometry(), this.border.generatePolylineGeometry());

          }
        }
      }

    }
    // add a border point
    else if (changingTo === true) {
      pointProcessingType = "border but passthrough";

      const testOutsider = this.isOutsider(pointId);
      if (testOutsider.truthy) {
        const nearestBorderPoints = this.findNearestBorderPoint(testOutsider.found!);
        if (nearestBorderPoints.length === 0) {
          // MessageCenter.addWarning(`The point's distance to the existing border is too far, ${warningAdvice}.`)
          await MessageCenter.addWarningFromCode("Survey_25");
          // testOutsider.found!.tinable = 0;
          this.borderRebuild = true;
          pointProcessingType = "border and modify";

        } else if (nearestBorderPoints.length === 1) {
          // MessageCenter.addWarning(`The point's distance to one of the nearest border points on the existing border is too far, ${warningAdvice}.`)
          await MessageCenter.addWarningFromCode("Survey_25");
          // testOutsider.found!.tinable = 0;
          this.borderRebuild = true;
          pointProcessingType = "border and modify";

        } else if (nearestBorderPoints.length > 1) {
          const newBorder = new SurfaceBorder();
          newBorder.wkid = this.border.wkid;
          newBorder.borderPoints.push(...this.border.borderPoints);

          const spatialReference = libraryStore.projectSpatialReference;
          for (let i = 1; i < nearestBorderPoints.length; i++) {
            const startIndex = Math.min(nearestBorderPoints[0].index, nearestBorderPoints[i].index);
            const endIndex = Math.max(nearestBorderPoints[0].index, nearestBorderPoints[i].index);

            // construction
            const startPoint = nearestBorderPoints[0].index < nearestBorderPoints[i].index ? nearestBorderPoints[0].groundPoint : nearestBorderPoints[i].groundPoint;
            const endPoint = nearestBorderPoints[0].index < nearestBorderPoints[i].index ? nearestBorderPoints[i].groundPoint : nearestBorderPoints[0].groundPoint;


            const paths = [[[startPoint.x, startPoint.y], [endPoint.x, endPoint.y]]];
            const baseLine = new Polyline({ hasZ: false, hasM: false, paths, spatialReference })
            const rightSide = esriGeometryEngine.offset(baseLine, designStore.maxAddBorderPointDistance, "feet", "square") as Polyline;
            const leftSide = esriGeometryEngine.offset(baseLine, -designStore.maxAddBorderPointDistance, "feet", "square") as Polyline;

            const rings = [[[leftSide.getPoint(0, 0).x, leftSide.getPoint(0, 0).y], [leftSide.getPoint(0, 1).x, leftSide.getPoint(0, 1).y], [rightSide.getPoint(0, 1).x, rightSide.getPoint(0, 1).y], [rightSide.getPoint(0, 0).x, rightSide.getPoint(0, 0).y]]]
            const offsetPolygon = new Polygon({ rings, spatialReference })

            const newPoint = testOutsider.found!.generateGeometry(spatialReference!)
            if (!esriGeometryEngine.contains(offsetPolygon, newPoint)) {
              continue;

            }

            // // test offset polygon - begin            
            // const symbol = new SimpleFillSymbol({ color: 'red', outline: { color: 'black', width: 1 } });
            // const graphic = new Graphic();
            // graphic.geometry = offsetPolygon;
            // graphic.symbol = symbol;

            // this.testShowOffset(graphic);
            // // test offset polygon - end

            // insert the new point - begin
            if (startIndex === 0 && endIndex > Math.ceil(this.border.borderPoints.length / 2)) {
              if (testOutsider.found!.id === this.orginalBorder?.borderPoints[0].id) {
                newBorder.borderPoints.splice(0, 0, testOutsider.found!);
              } else {
                newBorder.borderPoints.splice(endIndex + 1, this.border.borderPoints.length - (endIndex + 1), testOutsider.found!);
              }
            } else {
              newBorder.borderPoints.splice(startIndex + 1, endIndex - (startIndex + 1), testOutsider.found!);
            }
            // insert the new point - end

            // test self-crossed
            if (this.testSelfCrossing(newBorder, startIndex)) {
              // MessageCenter.addWarning(`The border shape for the area is too complex, ${warningAdvice}.`)
              await MessageCenter.addWarningFromCode("Survey_25");
              // testOutsider.found!.tinable = 0;
              this.borderRebuild = true;
              pointProcessingType = "border and modify";

              break;

            } else {
              this.border = newBorder;
              if (this.isCurrentIdentiticalToOriginalBorder()) {
                this.clearBorderSyncStatus();
              }

              pointProcessingType = "border and modify";
              this.applyBorderOnSurveyEditables(this.border.generatePolygonGeometry(), this.border.generatePolylineGeometry());

              break;

            }
          }

          if (pointProcessingType === "border but passthrough") {
            // MessageCenter.addWarning(`Cannot find connectable border points in the set distance or the border shape for the area is too complex, ${warningAdvice}.`)
            await MessageCenter.addWarningFromCode("Survey_25");
            // testOutsider.found!.tinable = 0;
            this.borderRebuild = true;
            pointProcessingType = "border and modify";
          }
        }
      }
    }
    return pointProcessingType;
  }

  isCurrentIdentiticalToOriginalBorder() {
    let resVal = true;
    if (this.border.borderPoints.length === this.orginalBorder?.borderPoints.length) {
      // if (!esriGeometryEngine.equals(this.border.generatePolylineGeometry(), this.orginalBorder.generatePolylineGeometry())) {
      //   resVal = false;
      // }
      for (let index = 0; index < this.border.borderPoints.length; index++) {
        if (this.border.borderPoints[index].id !== this.orginalBorder.borderPoints[index].id) {
          resVal = false;
          break;
        }
      }
    } else {
      resVal = false;
    }
    return resVal;
  }

  public async checkBreakLineCrosses(
    breakline: SurfaceBreakline,
    mapView: MapView
  ): Promise<boolean> {
    let crosses = false;
    const newBreaklineGeom = breakline.generateGeometry(mapView.spatialReference);

    // Check if crosses other breaklines
    for (const existingBreakline of this.breaklines) {
      // Skip the same breakline
      if (breakline.breaklineId === existingBreakline.breaklineId) {
        continue;
      }

      const existingBreaklineGeom = existingBreakline.generateGeometry(mapView.spatialReference);
      if (esriGeometryEngine.crosses(newBreaklineGeom, existingBreaklineGeom)) {
        crosses = true;
        break;
      }
    }

    // Check if self-intersecting
    if (doesPolylineSelfIntersect(breakline.groundPoints, mapView.spatialReference)) crosses = true;

    if (crosses) {
      MessageCenter.clearMessages();
      await MessageCenter.addErrorFromCode('Breakline_5');
    }

    return crosses;
  }
}
